import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useUserRole } from "../../utils/hooks/useUserRole";
import { SalesListFilters } from "./salesListFilters";
import {
  FIRST_PAGE,
  SalesListUrlState,
  readSalesListUrlState,
  writeSalesListUrlState,
} from "./salesListUrl";

const toSearch = (state: SalesListUrlState): string =>
  writeSalesListUrlState(state).toString();

/**
 * The การขาย list's filters, page and page size: seeded from the query string and written back
 * to it with `replace`, so the history entry the user returns to is the list as they left it, not
 * every keystroke on the way.
 *
 * Two one-way syncs, as on ETaxDetailsPage. State → URL runs whenever the state changes; URL →
 * state only when the URL differs from what this hook last wrote (the sidebar's plain `/sales`
 * link while already on the list, or back / forward between two list entries). Keeping the two
 * triggers apart is what stops one from writing stale values over the other.
 */
export function useSalesListUrlState() {
  const { isHeadOfficeAccounting } = useUserRole();
  const canPickBranch = Boolean(isHeadOfficeAccounting);
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<SalesListUrlState>(() =>
    readSalesListUrlState(searchParams, canPickBranch),
  );

  const searchString = searchParams.toString();
  const listSearch = toSearch(state);
  const lastWrittenSearch = useRef<string>(searchString);
  const currentSearch = useRef<string>(searchString);
  currentSearch.current = searchString;
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  // Declared before the state → URL effect so that on mount it compares against the URL as it
  // was opened, before that effect records the canonical rewrite as its own.
  useEffect(() => {
    if (searchString === lastWrittenSearch.current) return;
    lastWrittenSearch.current = searchString;
    const fromUrl = readSalesListUrlState(
      new URLSearchParams(searchString),
      canPickBranch,
    );
    // The same list spelled differently keeps the current state, so nothing refetches.
    setState((current) =>
      toSearch(current) === toSearch(fromUrl) ? current : fromUrl,
    );
  }, [searchString, canPickBranch]);

  useEffect(() => {
    if (listSearch === currentSearch.current) return;
    lastWrittenSearch.current = listSearch;
    setSearchParamsRef.current(listSearch, { replace: true });
  }, [listSearch]);

  /** Any filter change starts again from the first page: the old page pointed into another result. */
  const changeFilters = useCallback((patch: Partial<SalesListFilters>) => {
    setState((current) => ({
      ...current,
      filters: { ...current.filters, ...patch },
      page: FIRST_PAGE,
    }));
  }, []);

  const setPage = useCallback((page: number) => {
    setState((current) => ({ ...current, page }));
  }, []);

  const setLimit = useCallback((limit: number) => {
    setState((current) => ({ ...current, limit }));
  }, []);

  return {
    filters: state.filters,
    page: state.page,
    limit: state.limit,
    listSearch,
    changeFilters,
    setPage,
    setLimit,
  };
}
